import { z } from "zod";
import { CHANNEL_KEYS } from "../channels.js";

export const ONBOARDING_STEPS = ["welcome", "channels", "connections", "rules", "modules", "summary"] as const;
export type OnboardingStep = (typeof ONBOARDING_STEPS)[number];

export const ONBOARDING_STEP_LABELS: Record<OnboardingStep, string> = {
  welcome: "Hoş geldiniz",
  channels: "Kanallar",
  connections: "Bağlantı bilgileri",
  rules: "Veri aktarım kuralları",
  modules: "Modüller",
  summary: "Özet ve başlat",
};

export const MODULE_KEYS = ["orders", "shipments", "products", "inventory", "reports", "notifications", "automations"] as const;
export type ModuleKey = (typeof MODULE_KEYS)[number];

export const MODULE_LABELS: Record<ModuleKey, { label: string; description: string }> = {
  orders: { label: "Sipariş yönetimi", description: "Tüm kanalların siparişleri tek havuzda." },
  shipments: { label: "Kargo", description: "Hazırlama, paketleme, takip numarası." },
  products: { label: "Ürün kataloğu", description: "Ürün, varyant ve kanal eşleştirmeleri." },
  inventory: { label: "Stok ve fiyat", description: "Kanal bazlı stok/fiyat yayınlama." },
  reports: { label: "Raporlama", description: "Satış analitiği ve zamanlanmış raporlar." },
  notifications: { label: "Bildirimler", description: "Kritik stok, gecikme, entegrasyon hataları." },
  automations: { label: "Otomasyon", description: "EĞER → O ZAMAN kuralları." },
};

export const welcomeStepSchema = z.object({
  companyName: z.string().trim().min(2, { error: "Firma adı en az 2 karakter" }).max(160),
  legalName: z.string().trim().max(200).optional().or(z.literal("")),
  currency: z.enum(["TRY"]),
  timezone: z.enum(["Europe/Istanbul"]),
});

export const channelsStepSchema = z.object({
  selected: z.array(z.enum(CHANNEL_KEYS)).max(CHANNEL_KEYS.length),
});

export const rulesStepSchema = z.object({
  historyDays: z.union([z.literal(7), z.literal(30), z.literal(90)]),
  stockSource: z.enum(["ideasoft", "trendyol", "internal"]),
  channelSpecificPrices: z.boolean(),
  matchStrategy: z.enum(["barcode", "sku", "barcode_then_sku"]),
});

export const modulesStepSchema = z.object({
  enabled: z.array(z.enum(MODULE_KEYS)).min(1, { error: "En az bir modül seçin" }),
});

export const DEFAULT_RULES: z.infer<typeof rulesStepSchema> = {
  historyDays: 30,
  stockSource: "internal",
  channelSpecificPrices: true,
  matchStrategy: "barcode_then_sku",
};

export const onboardingDataSchema = z.object({
  welcome: welcomeStepSchema.partial().optional(),
  channels: channelsStepSchema.optional(),
  /** Kanal → "atlandı" işareti. Bağlantı durumu Integration kaydından okunur. */
  skippedConnections: z.array(z.enum(CHANNEL_KEYS)).optional(),
  rules: rulesStepSchema.optional(),
  modules: modulesStepSchema.optional(),
});
export type OnboardingData = z.infer<typeof onboardingDataSchema>;

export const saveStepSchema = z.discriminatedUnion("step", [
  z.object({ step: z.literal("welcome"), data: welcomeStepSchema }),
  z.object({ step: z.literal("channels"), data: channelsStepSchema }),
  z.object({ step: z.literal("connections"), data: z.object({ skipped: z.array(z.enum(CHANNEL_KEYS)) }) }),
  z.object({ step: z.literal("rules"), data: rulesStepSchema }),
  z.object({ step: z.literal("modules"), data: modulesStepSchema }),
]);
export type SaveStepInput = z.infer<typeof saveStepSchema>;
